import axios from 'axios';
import Cookie from 'js-cookie';

// How long before the JWT `exp` we proactively refresh. Must stay well below
// the access-token TTL, otherwise the watcher would refresh in a tight loop.
const REFRESH_SKEW_MS = 60 * 1000;
const MAX_TIMER_MS = 0x7fffffff; // setTimeout overflows above 2^31-1

export function getTokenExpiryMs(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch (_) {
    return null;
  }
}

// Tokens without a readable `exp` are treated as non-expiring here; the
// server's 401 (handled in axiosInterceptor) stays the source of truth.
export function isTokenExpired(token, skewMs = 0) {
  if (!token) return false;
  const exp = getTokenExpiryMs(token);
  if (exp === null) return false;
  return Date.now() >= exp - skewMs;
}

// `redirecting` guards against multiple concurrent 401s each firing their own
// redirect (e.g. when a page fans out several API calls in parallel).
// Once we decide to send the user back to /login we ignore every further 401.
let redirecting = false;

export function forceReLogin() {
  if (redirecting) return;
  redirecting = true;
  localStorage.removeItem('access-token');
  localStorage.removeItem('refresh-token');
  Cookie.remove('access-token');
  if (window.location.pathname !== '/login') {
    // replace() so the browser's back button doesn't bounce the user back
    // onto the broken authenticated page after they re-login.
    window.location.replace('/login');
  }
}

// Single-flight: concurrent callers (parallel 401s, watcher, focus handler)
// share one refresh request, so a rotated refresh-token is never reused.
let refreshPromise = null;

export function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  const refreshToken = localStorage.getItem('refresh-token');
  if (!refreshToken) {
    return Promise.reject(new Error('no refresh token'));
  }
  refreshPromise = axios
    .post(`${process.env.REACT_APP_BACKEND_URL}/auth/refresh`, {
      refresh_token: refreshToken
    })
    .then(res => {
      const payload = res.data.payload;
      localStorage.setItem('access-token', payload.token);
      localStorage.setItem('refresh-token', payload.refresh_token);
      Cookie.set('access-token', payload.token);
      window.dispatchEvent(new Event('access-token-updated'));
      return payload.token;
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

let watcherStarted = false;
let watchTimer = null;

async function checkTokenNow() {
  const token = localStorage.getItem('access-token');
  if (!token) return;
  if (isTokenExpired(token, REFRESH_SKEW_MS)) {
    try {
      await refreshAccessToken();
    } catch (_) {
      forceReLogin();
      return;
    }
  }
  scheduleNextCheck();
}

function scheduleNextCheck() {
  clearTimeout(watchTimer);
  const exp = getTokenExpiryMs(localStorage.getItem('access-token'));
  if (exp === null) return;
  // Fire just before expiry. Browsers throttle background-tab timers, so the
  // visibility/focus listeners below are the reliable wake-up path.
  const delay = Math.min(
    Math.max(exp - REFRESH_SKEW_MS - Date.now(), 5000),
    MAX_TIMER_MS
  );
  watchTimer = setTimeout(checkTokenNow, delay);
}

// Keeps an idle tab from sitting on an expired session: refreshes the token
// just before it expires and re-validates whenever the tab regains focus.
export function startTokenWatcher() {
  if (watcherStarted) return;
  watcherStarted = true;
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) checkTokenNow();
  });
  window.addEventListener('focus', checkTokenNow);
  // Reschedule whenever a new token lands (login or silent refresh).
  window.addEventListener('access-token-updated', scheduleNextCheck);
  checkTokenNow();
}
